import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    SurveyRecordItem,
    CreateSurveyRecordPayload,
    UpdateSurveyRecordResponsePayload,
    CurrentQuarterSurveyState,
} from '../types'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

const BASE_URL = '/surveys'

export const getSurveyRecords = async (payload: DataGridPayload) => {
    const { filters, ...rest } = payload
    const queryParams = {
        ...rest,
        filters: filters || undefined,
    }

    const response = await datacentral.get<ResponseArray<SurveyRecordItem>>(
        `${BASE_URL}?${qs.stringify(queryParams)}`
    )

    return {
        results: response.data.results,
        pagination: response.data.pagination,
    }
}

export const exportSurveyRecords = async (payload: DataGridPayload) => {
    const { filters, ...rest } = payload
    const queryParams = {
        ...rest,
        filters: filters || undefined,
        export: 'excel',
    }

    const response = await datacentral.get(
        `${BASE_URL}?${qs.stringify(queryParams)}`,
        { responseType: 'blob' }
    )
    return response.data as Blob
}

export const getSurveyRecord = async (
    id: number | string
): Promise<SurveyRecordItem> => {
    const response = await datacentral.get<ResponseObject<SurveyRecordItem>>(
        `${BASE_URL}/${id}`
    )
    return response.data.result
}

export const createSurveyRecord = async (
    payload: CreateSurveyRecordPayload
) => {
    const response = await datacentral.post<ResponseObject<unknown>>(
        BASE_URL,
        payload
    )
    return response.data
}

/**
 * Bulk create survey records in a single transaction
 * All records are created atomically - if any fails, none are saved
 */
export const createSurveyRecordsBulk = async (
    records: CreateSurveyRecordPayload[]
) => {
    const response = await datacentral.post<ResponseObject<unknown>>(
        `${BASE_URL}/bulk`,
        { records }
    )
    return response.data
}

/** Who was already surveyed this quarter, and what they answered. The answers
 *  matter: re-submitting a customer updates their record in place and the update
 *  is a full replace, so the form has to start from the existing values. */
export const getCurrentQuarterSurveyState = async (
    signal?: AbortSignal
): Promise<CurrentQuarterSurveyState> => {
    const response = await datacentral.get<
        ResponseObject<CurrentQuarterSurveyState>
    >(`${BASE_URL}/customer-ids/current-quarter`, { signal })
    return {
        customer_ids: response.data.result?.customer_ids ?? [],
        records: response.data.result?.records ?? [],
    }
}

export const updateSurveyRecordResponse = async (
    surveyRecordId: number | string,
    payload: UpdateSurveyRecordResponsePayload
) => {
    const response = await datacentral.patch<ResponseObject<unknown>>(
        `${BASE_URL}/${surveyRecordId}/responses`,
        payload
    )
    return response.data.result
}
